import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate, Navigate } from 'react-router-dom';
import { usePortalAuth } from '../context/PortalAuthContext';
import { House, FileText, Timer, Receipt, Warning, Ticket, User, SignOut, List, X, Car } from '@phosphor-icons/react';

const navItems = [
  { path: '/portal', end: true, icon: House, label: 'Übersicht' },
  { path: '/portal/long-term', icon: Timer, label: 'Langzeitmiete' },
  { path: '/portal/bookings', icon: FileText, label: 'Buchungen' },
  { path: '/portal/invoices', icon: Receipt, label: 'Rechnungen' },
  { path: '/portal/damages', icon: Warning, label: 'Schäden' },
  { path: '/portal/tickets', icon: Ticket, label: 'Strafzettel' },
  { path: '/portal/profile', icon: User, label: 'Profil' },
];

export default function PortalLayout() {
  const { customer, loading, logout } = usePortalAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-[#F7F8FA]"><div className="text-[#525866]">Wird geladen…</div></div>;
  }
  if (!customer) return <Navigate to="/portal/login" replace />;

  const isBusiness = customer.customer_type === 'business' || customer.company_name;
  const displayName = isBusiness ? customer.company_name : `${customer.first_name || ''} ${customer.last_name || ''}`.trim();

  const handleLogout = async () => {
    await logout();
    navigate('/portal/login');
  };

  return (
    <div className="min-h-screen bg-[#F7F8FA]" data-testid="portal-layout">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-[#E2E4E9] z-50 flex items-center justify-between px-4">
        <button onClick={() => setOpen(!open)} className="p-2 text-[#525866] hover:bg-[#F7F8FA] rounded" data-testid="portal-mobile-menu-btn">
          {open ? <X size={24} /> : <List size={24} />}
        </button>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-indigo-600 rounded flex items-center justify-center">
            <Car weight="fill" className="w-5 h-5 text-white" />
          </div>
          <span className="font-bold text-[#111418]">Kunden-Portal</span>
        </div>
        <div className="w-9" />
      </header>

      {open && <div className="lg:hidden fixed inset-0 bg-black/50 z-40" onClick={() => setOpen(false)} />}

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 h-full w-64 bg-white border-r border-[#E2E4E9] z-50 transform transition-transform duration-200 flex flex-col ${open ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        <div className="h-16 flex items-center gap-3 px-4 border-b border-[#E2E4E9]">
          <div className="w-10 h-10 bg-indigo-600 rounded flex items-center justify-center">
            <Car weight="fill" className="w-6 h-6 text-white" />
          </div>
          <div>
            <span className="font-bold text-[#111418] block">Kunden-Portal</span>
            <span className="text-xs text-[#8A94A6]">Ihr Vermieter-Konto</span>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto p-4 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.end}
              onClick={() => setOpen(false)}
              className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
              data-testid={`portal-nav-${item.label.toLowerCase()}`}
            >
              <item.icon weight="regular" size={20} />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-[#E2E4E9]">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-indigo-50 text-indigo-700 rounded flex items-center justify-center font-bold">
              {(displayName || 'K').charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-[#111418] truncate" data-testid="portal-current-user">{displayName || customer.email}</p>
              <p className="text-xs text-[#8A94A6] truncate">{customer.email}</p>
            </div>
          </div>
          <button onClick={handleLogout} className="sidebar-link w-full text-[#E60000] hover:bg-red-50" data-testid="portal-logout-btn">
            <SignOut weight="bold" size={20} />
            Abmelden
          </button>
        </div>
      </aside>

      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen">
        <Outlet />
      </main>
    </div>
  );
}
